import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2, RefreshCw, ShieldCheck, X } from 'lucide-react';
import {
  ClinicorpPendingPatientBinding,
  getClinicorpPendingPatientBindings,
  reconcileClinicorpPatientBinding,
  updateClinicorpPatientCreatePolicy,
} from '../services/clinicorpApi.ts';

interface ClinicorpPatientPolicyProps {
  clinicId: number;
  initialValue: boolean;
  canManage: boolean;
  isDark: boolean;
}

interface ActionModalProps {
  title: string;
  description: React.ReactNode;
  confirmText: string;
  busy: boolean;
  error: string | null;
  isDark: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const formatCreatedAt = (value: string): string | null => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString('pt-BR');
};

const ActionModal: React.FC<ActionModalProps> = ({
  title,
  description,
  confirmText,
  busy,
  error,
  isDark,
  onClose,
  onConfirm,
}) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();
    return () => previousFocus?.focus();
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && !busy) {
      onClose();
    }
    if (event.key === 'Tab') {
      const first = closeRef.current;
      const last = confirmRef.current;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="clinicorp-action-title"
        onKeyDown={handleKeyDown}
        className={`w-full max-w-md rounded-2xl border p-5 shadow-xl sm:p-6 ${
          isDark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-slate-200 bg-white text-slate-900'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <h3 id="clinicorp-action-title" className="text-lg font-semibold">{title}</h3>
          <button ref={closeRef} type="button" aria-label="Fechar" onClick={onClose} disabled={busy} className="rounded-lg p-2 disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className={`mt-3 text-sm leading-6 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
          {description}
        </div>
        {error && <p role="alert" className={`mt-3 text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>{error}</p>}
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={`min-h-11 rounded-xl border px-4 text-sm font-medium disabled:opacity-50 ${
              isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            Cancelar
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 text-sm font-medium text-white hover:bg-sminy-primary/90 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const ClinicorpPatientPolicy: React.FC<ClinicorpPatientPolicyProps> = ({
  clinicId,
  initialValue,
  canManage,
  isDark,
}) => {
  const [enabled, setEnabled] = useState(initialValue);
  const [pendingPolicy, setPendingPolicy] = useState<boolean | null>(null);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [bindings, setBindings] = useState<ClinicorpPendingPatientBinding[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [patientIds, setPatientIds] = useState<Record<number, string>>({});
  const [selectedBinding, setSelectedBinding] = useState<ClinicorpPendingPatientBinding | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileError, setReconcileError] = useState<string | null>(null);

  const loadBindings = useCallback(async (cursor = 0) => {
    setQueueLoading(true);
    setQueueError(null);
    if (cursor === 0) {
      setBindings([]);
      setNextCursor(null);
    }
    try {
      const page = await getClinicorpPendingPatientBindings(clinicId, cursor);
      setBindings((current) => cursor === 0 ? page.items : [
        ...current,
        ...page.items.filter((item) => !current.some((existing) => existing.binding_id === item.binding_id)),
      ]);
      setNextCursor(page.next_cursor);
    } catch (error) {
      setQueueError(error instanceof Error ? error.message : 'Não foi possível carregar as pendências.');
    } finally {
      setQueueLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    setEnabled(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (canManage) void loadBindings();
  }, [canManage, loadBindings]);

  const savePolicy = async () => {
    if (pendingPolicy === null || policySaving) return;
    setPolicySaving(true);
    setPolicyError(null);
    try {
      const saved = await updateClinicorpPatientCreatePolicy(clinicId, {
        ignore_same_name_on_patient_create: pendingPolicy,
      });
      setEnabled(saved.ignore_same_name_on_patient_create);
      setPendingPolicy(null);
    } catch (error) {
      setPolicyError(error instanceof Error ? error.message : 'Não foi possível salvar a regra.');
    } finally {
      setPolicySaving(false);
    }
  };

  const confirmReconciliation = (binding: ClinicorpPendingPatientBinding) => {
    const rawPatientId = (patientIds[binding.binding_id] || '').trim();
    const parsed = Number(rawPatientId);
    if (!/^\d+$/.test(rawPatientId) || !Number.isSafeInteger(parsed) || parsed <= 0) {
      setQueueError('Informe um PatientId numérico válido antes de conciliar.');
      return;
    }
    setSelectedBinding(binding);
    setSelectedPatientId(parsed);
    setReconcileError(null);
  };

  const reconcile = async () => {
    if (!selectedBinding || selectedPatientId === null || reconciling) return;
    setReconciling(true);
    setReconcileError(null);
    try {
      await reconcileClinicorpPatientBinding(clinicId, selectedBinding.binding_id, selectedPatientId);
      setPatientIds((current) => {
        const updated = { ...current };
        delete updated[selectedBinding.binding_id];
        return updated;
      });
      setSelectedBinding(null);
      setSelectedPatientId(null);
      await loadBindings();
    } catch (error) {
      setReconcileError(error instanceof Error ? error.message : 'Não foi possível conciliar este paciente.');
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className={`rounded-2xl border p-4 sm:p-5 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-slate-200 bg-slate-50'}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-sminy-primary" />
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-slate-800'}`}>
                Cadastro de pacientes com nome repetido
              </h3>
            </div>
            <p className={`mt-2 text-sm leading-6 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
              Se não houver vínculo seguro por identidade e telefone, permita criar um novo paciente mesmo quando o Clinicorp já tiver esse nome. A consulta só é confirmada após o Clinicorp devolver os identificadores e o CRM salvar o agendamento.
            </p>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              Desativada, um conflito de nome interrompe a marcação para pedir o nome completo ou encaminhar à equipe.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-label="Permitir criar pacientes com nome já cadastrado"
            aria-checked={enabled}
            disabled={!canManage || policySaving}
            onClick={() => { setPolicyError(null); setPendingPolicy(!enabled); }}
            className={`relative inline-flex min-h-11 min-w-16 shrink-0 items-center rounded-full border-2 p-1 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sminy-primary disabled:cursor-not-allowed disabled:opacity-50 ${
              enabled ? 'border-sminy-primary bg-sminy-primary' : (isDark ? 'border-gray-500 bg-gray-600' : 'border-slate-300 bg-slate-300')
            }`}
          >
            <span className={`h-6 w-6 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-7' : 'translate-x-0'}`} />
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
            enabled
              ? (isDark ? 'bg-green-900/50 text-green-200' : 'bg-green-100 text-green-800')
              : (isDark ? 'bg-gray-600 text-gray-200' : 'bg-slate-200 text-slate-700')
          }`}>{enabled ? 'Ativada' : 'Desativada'}</span>
          {!canManage && <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Somente gestores podem alterar esta regra.</span>}
        </div>
        {policyError && pendingPolicy === null && <p role="alert" className={`mt-3 text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>{policyError}</p>}
      </section>

      {canManage && (
        <section className={`rounded-2xl border p-4 sm:p-5 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-slate-800'}`}>Cadastros para conciliar</h3>
              <p className={`mt-1 text-xs leading-5 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                Quando a resposta da criação é incerta, confira o PatientId no Clinicorp. A conciliação valida nome e telefone; ela não cria outro paciente nem agenda a consulta.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void loadBindings()}
              disabled={queueLoading || reconciling}
              className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border px-3 text-xs font-medium disabled:opacity-50 ${
                isDark ? 'border-gray-500 text-gray-200 hover:bg-gray-600' : 'border-slate-300 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <RefreshCw className={`h-4 w-4 ${queueLoading ? 'animate-spin' : ''}`} /> Atualizar
            </button>
          </div>

          {queueError && <p role="alert" className={`mt-3 text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>{queueError}</p>}
          {queueLoading && bindings.length === 0 && <p className={`mt-4 text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>Carregando pendências...</p>}
          {!queueLoading && !queueError && bindings.length === 0 && <p className={`mt-4 text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>Nenhum cadastro pendente de conciliação.</p>}

          {bindings.length > 0 && (
            <div className="mt-4 space-y-3">
              {bindings.map((binding) => {
                const actionable = binding.status === 'unknown' && binding.lead_id !== null;
                return (
                  <div key={binding.binding_id} className={`rounded-xl border p-3 ${isDark ? 'border-gray-600 bg-gray-800/60' : 'border-slate-200 bg-white'}`}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className={`break-words text-sm font-medium ${isDark ? 'text-gray-100' : 'text-slate-900'}`}>{binding.requested_name}</p>
                        <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          Telefone {binding.phone_masked} · {binding.lead_id === null ? 'Lead excluído' : `Lead #${binding.lead_id}`} · Pendência #{binding.binding_id}
                        </p>
                        {formatCreatedAt(binding.created_at) && <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Registrada em {formatCreatedAt(binding.created_at)}</p>}
                      </div>
                      <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-medium ${
                        actionable
                          ? (isDark ? 'bg-amber-900/50 text-amber-200' : 'bg-amber-100 text-amber-800')
                          : (isDark ? 'bg-gray-600 text-gray-200' : 'bg-slate-200 text-slate-700')
                      }`}>{actionable ? 'Conciliação necessária' : 'Revisão da clínica'}</span>
                    </div>
                    {!actionable && <p className={`mt-2 text-xs ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                      {binding.lead_id === null
                        ? 'O lead foi excluído. O vínculo permanece protegido; encaminhe esta pendência à equipe responsável.'
                        : 'A criação ainda pode estar em andamento. Atualize a lista antes de tentar conciliar.'}
                    </p>}
                    {actionable && (
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                        <div className="w-full sm:max-w-48">
                          <label htmlFor={`clinicorp-patient-id-${binding.binding_id}`} className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>PatientId conferido</label>
                          <input
                            id={`clinicorp-patient-id-${binding.binding_id}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="off"
                            value={patientIds[binding.binding_id] || ''}
                            onChange={(event) => setPatientIds((current) => ({ ...current, [binding.binding_id]: event.target.value }))}
                            className={`min-h-10 w-full rounded-xl border px-3 text-sm focus:border-sminy-primary focus:outline-none focus:ring-2 focus:ring-sminy-primary/30 ${
                              isDark ? 'border-gray-500 bg-gray-800 text-gray-100' : 'border-slate-300 bg-white text-slate-900'
                            }`}
                          />
                        </div>
                        <button type="button" onClick={() => confirmReconciliation(binding)} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-sminy-primary px-3 text-sm font-medium text-white hover:bg-sminy-primary/90">
                          <Check className="h-4 w-4" /> Conferir e vincular
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {nextCursor !== null && (
            <button type="button" disabled={queueLoading} onClick={() => void loadBindings(nextCursor)} className={`mt-4 min-h-10 rounded-xl border px-3 text-sm font-medium disabled:opacity-50 ${isDark ? 'border-gray-500 text-gray-200' : 'border-slate-300 text-slate-700'}`}>
              {queueLoading ? 'Carregando...' : 'Mostrar mais pendências'}
            </button>
          )}
        </section>
      )}

      {pendingPolicy !== null && (
        <ActionModal
          title={pendingPolicy ? 'Ativar criação com nome repetido?' : 'Desativar criação com nome repetido?'}
          description={pendingPolicy
            ? 'Quando não houver vínculo seguro, novas marcações poderão criar um paciente com nome já cadastrado. O agente continuará pedindo nome completo e validando identidade e horário.'
            : 'Um conflito de nome voltará a bloquear a criação do paciente. O agente pedirá o nome completo e encaminhará casos persistentes à equipe.'}
          confirmText={pendingPolicy ? 'Ativar regra' : 'Desativar regra'}
          busy={policySaving}
          error={policyError}
          isDark={isDark}
          onClose={() => { if (!policySaving) setPendingPolicy(null); }}
          onConfirm={() => void savePolicy()}
        />
      )}

      {selectedBinding && selectedPatientId !== null && (
        <ActionModal
          title="Vincular PatientId verificado?"
          description={<>Confira no Clinicorp se o PatientId <strong>{selectedPatientId}</strong> corresponde a <strong>{selectedBinding.requested_name}</strong> e ao telefone {selectedBinding.phone_masked}. O sistema validará os dados novamente antes de salvar o vínculo. Esta ação não agenda a consulta.</>}
          confirmText="Validar e vincular"
          busy={reconciling}
          error={reconcileError}
          isDark={isDark}
          onClose={() => { if (!reconciling) { setSelectedBinding(null); setSelectedPatientId(null); } }}
          onConfirm={() => void reconcile()}
        />
      )}
    </div>
  );
};

export default ClinicorpPatientPolicy;
